import React, { useState, useEffect } from 'react';
import { Step } from 'react-joyride';
import { useHistory } from 'react-router-dom';

import AppBar from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import ConfirmBottomSheet from 'components/ConfirmBottomSheet';
import GroupedList from 'components/GroupedList';
import GroupedItem from 'components/GroupedList/components/GroupedItem';
import Loader from 'components/Loader';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import Search from 'components/Search';
import Tour from 'components/Tour';
import useIsMobile from 'hooks/useIsMobile';
import useSelectionOrigin from 'hooks/useSelectionOrigin';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { Group } from 'models/group';
import { GroupedListModel } from 'models/groupedList';
import GroupFormContainer from 'screens/containers/Group';
import { htmlT } from 'utils/generic';

import GroupEmptyList from '../GroupList/components/GroupEmptyList';
import {
  List,
  Container,
  Content,
  BoxContainer,
  ButtonGhost,
  ButtonContainer,
  StyledButton,
  SubTitle,
  AppBarCenter,
} from './styles';

interface Props {
  groupTarget?: string;
  groupList: GroupedListModel[];
  isLoading: boolean;
  tourHasAccess: boolean;
  tourLoading: boolean;
  isAlphabetic?: boolean;
  showGroupsSelection: boolean;
  currentGroup?: GroupedListModel[];
  selectedGroupsRules?: any;
  onConfirmSelection?: (group: GroupedListModel[]) => void;
  onCloseSelectionScreen: () => void;
  noAdd?: boolean;
  singleSelect?: boolean;
  changeModalTitleKey: string;
  changeModalMessageKey: string;
}

const GroupSelectList = ({
  groupList,
  isAlphabetic,
  showGroupsSelection,
  isLoading,
  tourHasAccess,
  tourLoading,
  currentGroup,
  onCloseSelectionScreen,
  onConfirmSelection,
  groupTarget,
  selectedGroupsRules,
  noAdd,
  singleSelect,
  changeModalTitleKey,
  changeModalMessageKey,
}: Props) => {
  const [showSearch, setShowSearch] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<GroupedListModel[]>([]);
  const [groupResult, setRulesResult] = useState<GroupedListModel[]>([]);
  const [currentGroupSelect, setCurrentGroupSelect] = useState<GroupedListModel>();
  const [showGroup, setShowGroup] = useState(false);
  const [showBottomModal, setShowBottomModal] = useState(false);
  const history = useHistory();
  const { t } = useStoreTranslation();
  const isMobile = useIsMobile();
  const groupId = '';

  const { itemCreated, updateItemCreated } = useSelectionOrigin();
  const buttonText = t('groupList.selectGroups', { count: selectedGroup.length });

  const searchResultsText = htmlT(t, 'groupList.searchResults', { count: groupResult?.length });
  const groupListAfterSelected = groupList.filter((user) => !selectedGroup.find((selected) => user.id === selected.id));

  const handleSelection = (group: GroupedListModel) => {
    const groupFound = selectedGroup.find((item) => item.id === group.id);
    setCurrentGroupSelect(group);

    if (group.id && (currentGroup || []).length > 0 && !(currentGroup || []).find((g) => g.id === group.id)) {
      setShowBottomModal(true);
      return;
    }

    if (groupFound) {
      if (singleSelect) return;
      setSelectedGroup(selectedGroup.filter((item) => item.id !== group.id));
      return;
    }

    if (singleSelect) {
      setSelectedGroup([group]);
      return;
    }
    setSelectedGroup([...selectedGroup, group]);
  };

  const handleOnSearch = (value: string) => {
    if (value) {
      setRulesResult(
        groupList.filter(
          (name: GroupedListModel) => name.name && name.name.toLowerCase().includes(value.toLowerCase()),
        ),
      );
    } else setRulesResult([]);
  };

  const handleSubmitSelection = () => {
    if (onConfirmSelection) onConfirmSelection(selectedGroup);
    updateItemCreated(null);
    onCloseSelectionScreen();
  };

  const handleSelectionConfirm = () => {
    if (!currentGroupSelect) return;

    if (singleSelect) setSelectedGroup([currentGroupSelect]);
    else setSelectedGroup([...selectedGroup, currentGroupSelect]);
  };

  const handleNavigateHistory = () => {
    if (showGroupsSelection) {
      updateItemCreated(null);
      if (onCloseSelectionScreen) onCloseSelectionScreen();
    } else {
      history.push('/dashboard');
    }
  };

  useEffect(() => {
    if (itemCreated && itemCreated.length > 0) return;

    if (selectedGroupsRules && selectedGroupsRules.length > 0) {
      setSelectedGroup(groupList.filter((user) => selectedGroupsRules.find((selected) => user.id === selected)));
    }
  }, [selectedGroupsRules, groupList]); // eslint-disable-line react-hooks/exhaustive-deps
  // TODO: check dependencies or refactor

  useEffect(() => {
    if (itemCreated) {
      setSelectedGroup([
        ...selectedGroup,
        ...itemCreated.map((group: Group) => ({
          id: group.id,
          name: group.name,
          badges: (group.users || []).map((badge) => ({ id: badge.id, name: badge.name, lastName: badge.lastName })),
        })),
      ]);
      setShowGroup(false);
    } else if (currentGroup) {
      setSelectedGroup(currentGroup);
    }
  }, [itemCreated, currentGroup]); // eslint-disable-line react-hooks/exhaustive-deps
  // TODO: check dependencies or refactor

  const renderAddGroup = () => (
    <ApplicationOverlayContent sidebar width={1000} show={showGroup} onClose={() => setShowGroup(false)}>
      <GroupFormContainer
        originRule={groupTarget}
        isFromSelection={showGroup}
        match={{ params: { groupId } }}
        onCloseFormGroup={() => setShowGroup(false)}
      />
    </ApplicationOverlayContent>
  );

  const renderList = () => (
    <>
      {!noAdd && (
        <>
          <BoxContainer>
            <ButtonGhost onClick={() => setShowGroup(true)}>
              <ButtonGhost.Icon src="/images/people-act.svg" />
              {t('group.header.create')}
            </ButtonGhost>
          </BoxContainer>
          {renderAddGroup()}
        </>
      )}
      <Search
        show={groupList.length >= 10}
        onShowSearch={(isSearch: boolean) => setShowSearch(isSearch)}
        onSearch={handleOnSearch}
        fullWidth
      />
      {showSearch ? (
        <List>
          <GroupedItem
            quantityText={searchResultsText}
            isSearch
            items={groupResult}
            selectedItems={selectedGroup}
            selectableType={singleSelect ? 'radio' : 'check'}
            onSelection={handleSelection}
          />
        </List>
      ) : (
        <>
          <GroupedList
            isAlphabetic={isAlphabetic}
            isSelectable
            items={singleSelect ? groupList : groupListAfterSelected}
            isSearch={showSearch}
            selectableType={singleSelect ? 'radio' : 'check'}
            onSelection={handleSelection}
            currentItem={selectedGroup}
            selectedQuantityKey="group.groupAggregation.groupSelected"
            singleSelect={singleSelect}
          />
        </>
      )}
    </>
  );

  const initialSteps: Step[] = [
    {
      target: '.target',
      content: t('groupList.addButtonTour'),
      disableBeacon: true,
      placement: 'center',
    },
  ];

  return (
    <>
      <Mobile>
        <AppBar goBack={handleNavigateHistory}>
          <AppBarCenter>
            <h5>{t('groupList.title')}</h5>
            <SubTitle>{groupTarget}</SubTitle>
          </AppBarCenter>
        </AppBar>
      </Mobile>
      <Container>
        {isLoading ? (
          <Loader fullScreen={isMobile} />
        ) : (
          <>
            <Desktop>
              <ModalHeader title={t('userMenu.groupsTitle')} />
            </Desktop>
            <Content hasList={groupList.length > 0}>
              <Tour
                type="cancelable"
                show={!tourLoading && !tourHasAccess}
                hideArrow
                steps={initialSteps}
                disableOverlayClose
                disableScrollParentFix
              />
              {groupList && groupList.length ? renderList() : <GroupEmptyList />}
            </Content>
            {((selectedGroup && selectedGroup.length > 0) || (selectedGroupsRules || []).length > 0) && (
              <ButtonContainer>
                <StyledButton onClick={handleSubmitSelection} value={buttonText} />
              </ButtonContainer>
            )}
            <ConfirmBottomSheet
              show={showBottomModal}
              title={t(changeModalTitleKey)}
              content={htmlT(t, changeModalMessageKey, { group: currentGroupSelect?.name })}
              onConfirm={handleSelectionConfirm}
              onClose={() => setShowBottomModal(false)}
            />
          </>
        )}
      </Container>
    </>
  );
};

export default GroupSelectList;
